import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBvd2VyJTIwcGxhbnQlMjB3aWRlJTIwYWVyaWFsJTIwdmlld3xlbnwwfHx8fDE3ODcyODUyOTR8MA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.12 },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = [t("hero.line1"), t("hero.line2"), t("hero.line3")];
  const figures = [
    { v: "250", u: "MWac", l: t("hero.k1") },
    { v: "533,75", u: "GWh/an", l: t("hero.k2") },
    { v: "40", u: "ans", l: t("hero.k3") },
  ];

  return (
    <section id="hero" ref={ref} className="relative min-h-screen overflow-hidden bg-slate-950">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Centrale solaire" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/50 to-slate-950/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-screen flex flex-col justify-center pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mono text-[11px] sm:text-xs tracking-[0.25em] uppercase text-sky-300 mb-8 flex items-center gap-3"
        >
          <span className="inline-block w-8 h-px bg-sky-400" />
          {t("hero.tag")}
        </motion.div>

        <h1 className="font-display text-white font-light tracking-tighter text-[13vw] sm:text-[9vw] lg:text-[6.5vw] leading-[0.98] max-w-5xl">
          {lines.map((ln, i) => (
            <span key={i} className="reveal-mask">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {ln}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-slate-200 text-base sm:text-lg leading-relaxed"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/projet"
            data-testid="hero-cta-brief"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-secondary text-white px-7 py-3.5 font-medium hover:bg-green-700 active:scale-95 transition-[background-color,transform] duration-200"
          >
            {t("hero.cta1")}
            <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-200" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-cta-partner"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-medium hover:bg-white/10 active:scale-95 transition-[background-color,transform] duration-200"
          >
            {t("hero.cta2")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25 }}
          className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl border-t border-white/15 pt-8"
        >
          {figures.map((f) => (
            <div key={f.l} data-testid="hero-figure">
              <div className="flex items-baseline gap-1">
                <span className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-white">{f.v}</span>
                <span className="mono text-xs sm:text-sm text-sky-300">{f.u}</span>
              </div>
              <div className="text-xs sm:text-sm text-slate-300 mt-1">{f.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
